// #region Imports
import { defineComponent, defineAsyncComponent } from "vue";
// #endregion
// #region Core
export default defineComponent({
    // #region Component
    name: 'g-quantity-field',
    mixins: [appHost.mixins.form],
    components: {
        DxNumberBox: defineAsyncComponent(async () => {
            return (await import(/* webpackChunkName: "dxCommon"*/ 'devextreme-vue/number-box')).DxNumberBox;
        }),
        DxSelectBox: defineAsyncComponent(async () => {
            return (await import(/* webpackChunkName: "dxCommon"*/ 'devextreme-vue/select-box')).DxSelectBox;
        })
    },
    // #endregion
    // #region Properties
    props: {
        placeholder: {
            type: String,
            required: false,
            default: ''
        },
        inputAttr: {
            type: Object,
            required: false,
            default: null,
        },
        entryMode: {
            type: String,
            required: false,
            default: 'numeric',
            validValues: ['numeric', 'numeric-step', 'numeric-list'],
            validator(value) {
                if (!value.length) {
                    return true;
                }
                return ['numeric', 'numeric-step', 'numeric-list'].includes(value);
            }
        },
        quantityStep: {
            type: Number,
            required: false,
            default: 1
        },
        quantityMin: {
            type: Number,
            required: false,
            default: null
        },
        quantityMax: {
            type: Number,
            required: false,
            default: null
        },
        quantityDcp: {
            type: Number,
            required: false,
            default: null
        },
        quantityUnit: {
            type: String,
            required: false,
            default: null
        },
        quantity: {
            type: Object,
            required: false,
            default: undefined
        },
        showUnitInDropdown: {
            type: Boolean,
            required: false,
            default: false
        },
        showClearButton: {
            type: Boolean,
            required: false,
            default: false
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false
        },
        largeSpin: {
            type: Boolean,
            required: false,
            default: false
        },
    },
    // #endregion
    // #region Computed
    computed: {
        // #region Common
        inputAttrInternal() {
            return this.inputAttr;
        },
        // #endregion
        // #region Component
        getComponent() {
            if (this._entryMode === 'numeric-list') {
                return "DxSelectBox";
            }
            else {
                return "DxNumberBox";
            }
        },
        getComponentOptions() {
            if (this._entryMode === 'numeric-list') {
                return {
                    valueChangeEvent: "keyup",
                    dataSource: this.getItems,
                    valueExpr: "value",
                    displayExpr: "caption",
                    readOnly: this.disabled,
                    dropDownOptions: {
                        minWidth: 0
                    },
                };
            }
            else {
                return {
                    disabled: this.disabled,
                    showSpinButtons: true,
                    useLargeSpinButtons: this.largeSpin,
                    format: this.numberFormat,
                    min: this._quantityMin,
                    max: this._quantityMax,
                    step: this._quantityStep ?? 1
                };
            }
        },
        getComponentEvents() {
            if (this._entryMode === 'numeric-step') {
                return {
                    valueChanged: this.numberBoxValueChanged
                };
            }
            else {
                return {};
            }
        },
        // #endregion
        // #region Number
        numberFormat() {
            if (!this._quantityDcp) {
                return null;
            }
            else {
                return { type: 'fixedPoint', precision: this._quantityDcp };
            }
        },
        // #endregion
        // #region Items
        getItems() {
            let tItems = [];
            let tMin = this._quantityMin;
            let tMax = this._quantityMax;
            let tStep = this._quantityStep;
            if (!tMin || tMin == 0) {
                tMin = 1;
            }
            if (!tMax || tMax == 0) {
                tMax = 1;
            }
            if (!tStep || tStep == 0) {
                tStep = 1;
            }
            while (true) {
                if (tMax > 0) {
                    if (tMin > tMax) {
                        break;
                    }
                }
                else if (tItems.length >= 100) {
                    break;
                }
                tItems.push({
                    value: tMin,
                    caption: utils.formatNumberWithDcp(tMin, this._quantityDcp) + (this._quantityUnit && this.showUnitInDropdown ? (" " + this._quantityUnit) : "")
                });
                tMin = tMin + tStep;
            }
            return tItems;
        },
        // #endregion
        // #region Quantity
        _entryMode() {
            if (this.quantity) {
                return this.quantity.entryMode ?? '';
            }
            else {
                return this.entryMode;
            }
        },
        _quantityUnit() {
            if (this.quantity) {
                return this.quantity.unit ?? '';
            }
            else {
                return this._quantityUnit;
            }
        },
        _quantityDcp() {
            if (this.quantity) {
                return this.quantity.unitDcp ?? '';
            }
            else {
                return this._quantityDcp;
            }
        },
        _quantityMin() {
            if (this.quantity) {
                return this.quantity.min ?? 1;
            }
            else {
                return this._quantityMin;
            }
        },
        _quantityMax() {
            if (this.quantity) {
                return this.quantity.max ?? undefined;
            }
            else {
                return this._quantityMax;
            }
        },
        _quantityStep() {
            if (this.quantity) {
                return this.quantity.step ?? 1;
            }
            else {
                return this._quantityStep;
            }
        }
        // #endregion
    },
    // #endregion
    // #region Methodes
    methods: {
        // #region Events
        onEnterKey(event) {
            this.$emit('EnterKey', event);
        },
        numberBoxValueChanged(e) {
            if (this._quantityStep === 0) {
                return;
            }
            let tNewValue = this.round(e.value, this._quantityStep);
            if (this._quantityMax > 0 && tNewValue > this._quantityMax) {
                tNewValue = this._quantityMax;
            }
            if (this._quantityMin > 0 && tNewValue < this._quantityMin) {
                tNewValue = this._quantityMin;
            }
            if (tNewValue !== e.value) {
                this.$refs.txtInput.instance.option('value', tNewValue);
            }
        },
        // #endregion
        // #region Helper
        round(num, pre) {
            return pre * Math.ceil(num / pre);
        }
        // #endregion
    }
    // #endregion
});
// #endregion
